import { useState } from "react";
import { useNavigate } from "react-router";
import { HoverCard } from "radix-ui";
import type { VoiceParticipant } from "@atrium/shared";
import { useStreamUi } from "./stores";
import { qualityLabel } from "./stream";
import { LiveBadge, ViewerCount, useStreamPreview } from "./StreamTile";
import { useVoice } from "./VoiceProvider";

/**
 * The Live marker in the channel list. Hover for a snapshot of the stream;
 * click to open the room (and start watching if you're in it).
 */
export function LiveStreamBadge({ p, communityId, participants }: { p: VoiceParticipant; communityId: string; participants: VoiceParticipant[] }) {
  const voice = useVoice();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const preview = useStreamPreview(p.socketId, open);
  const self = p.socketId === voice.selfSocketId;
  const inRoom = voice.channelId === p.channelId && voice.status === "connected";
  const name = self ? "Your stream" : `${p.user.displayName}'s stream`;

  const go = () => {
    if (inRoom && !self) voice.setWatching(p.socketId, true);
    else if (self) useStreamUi.getState().setFocused(p.socketId);
    navigate(`/c/${communityId}/${p.channelId}`);
  };

  return (
    <HoverCard.Root open={open} onOpenChange={setOpen} openDelay={250} closeDelay={80}>
      <HoverCard.Trigger asChild>
        <button
          type="button"
          onClick={go}
          aria-label={inRoom && !self ? `Watch ${name}` : `${name} is live`}
          className="shrink-0 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <LiveBadge />
        </button>
      </HoverCard.Trigger>
      <HoverCard.Portal>
        <HoverCard.Content side="right" align="start" sideOffset={10} collisionPadding={8} className="z-[60] w-72 overflow-hidden rounded-lg bg-surface shadow-pop animate-pop-in">
          <div className="flex aspect-video items-center justify-center bg-bg-subtle">
            {preview ? <img src={preview} alt={`Preview of ${name}`} className="size-full object-cover" draggable={false} /> : <span className="text-sm text-fg-3">Preview coming up…</span>}
          </div>
          <div className="flex items-center gap-2 px-3 py-2 text-sm">
            <span className="min-w-0 truncate font-medium">{self ? "You" : p.user.displayName}</span>
            {p.stream && <span className="shrink-0 text-fg-3 tabular">{qualityLabel(p.stream)}</span>}
            <ViewerCount stream={p} participants={participants} className="ml-auto text-fg-3" />
          </div>
          <p className="border-t border-line px-3 py-2 text-sm text-fg-3">{inRoom ? (self ? "Click to open your stream" : "Click to watch") : "Join the channel to watch"}</p>
        </HoverCard.Content>
      </HoverCard.Portal>
    </HoverCard.Root>
  );
}
